"use client";

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Avatar } from '@/components/ds';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Reply, Smile, Mic } from 'lucide-react';
import { useLiveTimestamp } from '@/hooks/useLiveTimestamp';
import { CommunityComment } from '@/types';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { insertEmojiAtCursor } from '@/lib/insertEmojiAtCursor';
import { useCaretRestore } from '@/hooks/useCaretRestore';
import VoiceRecorderInput from '@/components/VoiceRecorderInput';

// Deferred so `emoji-picker-react` stays out of the feed's initial bundle.
// The popover statically imports the library's Theme/EmojiStyle enums (runtime
// values, not types), so a static import here would pull the whole picker into
// every feed page even though the picker component itself is lazy.
const EmojiPickerPopover = dynamic(() => import('@/components/dm/EmojiPickerPopover'), {
  ssr: false,
});

interface PostCommentsProps {
  postId: string;
  postAuthorName: string;
  maxComments?: number; // Show first N comments, rest expand inline
  refreshTrigger?: number; // Increment this to refresh comments
  autoFocus?: boolean; // Auto-focus the comment input when component mounts
}

export default function PostComments({
  postId,
  postAuthorName,
  maxComments = 3,
  refreshTrigger,
  autoFocus = false
}: PostCommentsProps) {
  const { user } = useAuthContext();
  const { profile } = useUserProfile();
  const { toast } = useToast();
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [newComment, setNewComment] = useState('');
  const [composerFocused, setComposerFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Holds the id of the surface whose picker is open: 'new-comment' for the
  // top composer, or a comment id for that comment's reply box.
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);
  const [isVoiceMode, setIsVoiceMode] = useState<string | null>(null); // Stores the commentId being replied to by voice
  const [isVoiceModeNewComment, setIsVoiceModeNewComment] = useState(false);
  const [newCommentCaret, setNewCommentCaret] = useState<number | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  useCaretRestore(newCommentCaret, setNewCommentCaret, commentInputRef);

  useEffect(() => {
    loadComments();
  }, [postId, refreshTrigger]);

  useEffect(() => {
    if (autoFocus && commentInputRef.current) {
      setTimeout(() => {
        commentInputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const loadComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/community/posts/${postId}/comments`);
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setComments(data);
      } else {
        setComments([]);
        if (!response.ok) {
          console.warn('Failed to load comments:', data);
        }
      }
    } catch (err) {
      console.error('Error loading comments:', err);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!user) return;

    // Optimistic update
    setComments(prev => prev.map(comment => {
      if (comment.id === commentId) {
        return {
          ...comment,
          likes: comment.isLiked ? Math.max(0, comment.likes - 1) : comment.likes + 1,
          isLiked: !comment.isLiked
        };
      }
      return comment;
    }));

    try {
      const response = await fetch(`/api/community/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Update with server response
        setComments(prev => prev.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: result.likes ?? comment.likes,
              isLiked: result.liked ?? comment.isLiked
            };
          }
          return comment;
        }));
      } else {
        // Revert on error
        await loadComments();
      }
    } catch (error) {
      console.error('Error liking comment:', error);
      // Revert on error
      await loadComments();
    }
  };

  const handleSubmitNewComment = async () => {
    if (!newComment.trim() || !user || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        },
        body: JSON.stringify({
          content: newComment.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to post comment');
      }

      setNewComment('');
      await loadComments();

      toast({
        title: "Comment posted",
        description: "Your comment has been added",
        variant: "success",
      });
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!replyContent.trim() || !user || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        },
        body: JSON.stringify({
          content: replyContent.trim(),
          parentId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to post reply');
      }

      setReplyContent('');
      setReplyingTo(null);
      await loadComments();

      toast({
        title: "Reply posted",
        description: "Your reply has been added",
        variant: "success",
      });
    } catch (error) {
      console.error('Error posting reply:', error);
      toast({
        title: "Error",
        description: "Failed to post reply. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoiceSend = async (audioBlob: Blob, parentId: string) => {
    if (!user || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('audioFile', audioBlob, `voice_reply_${Date.now()}.webm`);
      formData.append('parentId', parentId);
      formData.append('postId', postId);

      const response = await fetch(`/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'x-user-id': user.id
          // NOTE: No 'Content-Type' header needed; browser sets it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send voice reply' }));
        throw new Error(errorData.error || 'Failed to send voice reply');
      }

      setIsVoiceMode(null);
      await loadComments();

      toast({
        title: "Voice reply sent",
        description: "Your voice reply has been posted",
        variant: "success",
      });
    } catch (error: any) {
      console.error('Error sending voice reply:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send voice reply. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewCommentEmoji = (emoji: string) => {
    const { next, caret } = insertEmojiAtCursor(commentInputRef.current, newComment, emoji);
    setNewComment(next);
    setNewCommentCaret(caret);
  };

  if (loading) {
    return <div className="mt-3 text-sm text-text-tertiary">Loading comments...</div>;
  }

  // Filter to only show top-level comments for display (nested ones will be shown recursively)
  const topLevelComments = comments.filter((c: CommunityComment) => !c.parentId);
  const displayComments = showAll ? topLevelComments : topLevelComments.slice(0, maxComments);
  const hasMore = topLevelComments.length > maxComments;

  // Composer is "active" when the textarea has focus or any content; the
  // emoji/mic/Post action row only renders in that state. Idle state is a
  // single-row input matching the visual weight of comment rows. The open
  // picker also pins it active — picking an emoji blurs the textarea, and
  // without this the action row (which anchors the picker) would unmount
  // mid-selection on an empty composer.
  const composerActive =
    composerFocused || newComment.length > 0 || showEmojiPicker === 'new-comment';

  // Shared, id-keyed state handed to every CommentItem at any nesting depth,
  // so a nested reply resolves its own reply/emoji/voice state instead of
  // inheriting its parent's.
  const shared: SharedCommentState = {
    allComments: comments,
    postAuthorName,
    postId,
    user,
    isSubmitting,
    onLike: handleLikeComment,
    replyingTo,
    setReplyingTo,
    replyContent,
    setReplyContent,
    onSubmitReply: handleSubmitReply,
    emojiTarget: showEmojiPicker,
    setEmojiTarget: setShowEmojiPicker,
    voiceTarget: isVoiceMode,
    setVoiceTarget: setIsVoiceMode,
    onVoiceSend: handleVoiceSend,
    refreshComments: loadComments,
  };

  return (
    <div className="mt-2.5 space-y-2.5 border-t border-border-subtle pt-2.5">
      {/* New Comment Input */}
      {user && (
        <div>
          <div className="flex items-start gap-2">
            <Avatar
              src={profile?.avatar_url || user.user_metadata?.avatar_url}
              name={profile?.display_name || profile?.username || '?'}
              size={28}
            />
            <div className="flex-1 min-w-0 space-y-1.5">
              <Textarea
                ref={commentInputRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onFocus={() => setComposerFocused(true)}
                onBlur={() => setComposerFocused(false)}
                placeholder="Write a comment..."
                rows={composerActive ? 3 : 1}
                className={cn(
                  'bg-surface-3 border-border-default text-text-primary placeholder:text-text-tertiary resize-none transition-[min-height]',
                  composerActive ? 'min-h-[72px]' : 'min-h-[36px] py-2'
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleSubmitNewComment();
                  }
                }}
              />
              {composerActive && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      ref={emojiButtonRef}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() =>
                        setShowEmojiPicker(showEmojiPicker === 'new-comment' ? null : 'new-comment')
                      }
                      aria-expanded={showEmojiPicker === 'new-comment'}
                      aria-haspopup="dialog"
                      className="w-7 h-7 grid place-items-center rounded-sm text-text-tertiary hover:text-text-secondary hover:bg-surface-3 transition-colors"
                      aria-label="Emoji"
                    >
                      <Smile className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setIsVoiceModeNewComment(true)}
                      className="w-7 h-7 grid place-items-center rounded-sm text-text-tertiary hover:text-accent hover:bg-surface-3 transition-colors"
                      aria-label="Record voice comment"
                      title="Record voice comment"
                    >
                      <Mic className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Button
                    size="sm"
                    className="h-7 px-3 text-xs bg-accent hover:bg-accent-hover text-text-on-accent font-normal"
                    onClick={handleSubmitNewComment}
                    disabled={!newComment.trim() || isSubmitting}
                  >
                    {isSubmitting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              )}
              {showEmojiPicker === 'new-comment' && (
                <EmojiPickerPopover
                  anchorRef={emojiButtonRef}
                  onSelect={handleNewCommentEmoji}
                  onClose={() => setShowEmojiPicker(null)}
                />
              )}
              {isVoiceModeNewComment && (
                <div className="mt-2">
                  <VoiceRecorderInput
                    onVoiceSend={(audioBlob: Blob) => {
                      // Handle voice comment upload
                      const formData = new FormData();
                      formData.append('audioFile', audioBlob, `voice_comment_${Date.now()}.webm`);
                      formData.append('content', '[Voice Comment]');

                      fetch(`/api/community/posts/${postId}/comments`, {
                        method: 'POST',
                        headers: {
                          'x-user-id': user.id
                        },
                        body: formData
                      }).then(() => {
                        setIsVoiceModeNewComment(false);
                        loadComments();
                      });
                    }}
                    onCancel={() => setIsVoiceModeNewComment(false)}
                    targetId={`new-comment-${postId}`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {displayComments.length === 0 ? (
        <div className="text-sm text-text-tertiary py-2">No comments yet. Be the first to comment!</div>
      ) : (
        displayComments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} {...shared} />
        ))
      )}
      {hasMore && !showAll && (
        <Button
          variant="ghost"
          size="sm"
          className="text-accent hover:text-accent-hover text-xs"
          onClick={() => setShowAll(true)}
        >
          View {topLevelComments.length - maxComments} more {topLevelComments.length - maxComments === 1 ? 'comment' : 'comments'}
        </Button>
      )}
      {showAll && hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="text-accent hover:text-accent-hover text-xs"
          onClick={() => setShowAll(false)}
        >
          Show fewer comments
        </Button>
      )}
    </div>
  );
}

interface SharedCommentState {
  allComments: CommunityComment[];
  postAuthorName: string;
  postId: string;
  user: any;
  isSubmitting: boolean;
  onLike: (commentId: string) => void;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  replyContent: string;
  setReplyContent: (content: string) => void;
  onSubmitReply: (parentId: string) => void;
  emojiTarget: string | null;
  setEmojiTarget: (id: string | null) => void;
  voiceTarget: string | null;
  setVoiceTarget: (id: string | null) => void;
  onVoiceSend: (audioBlob: Blob, parentId: string) => void;
  refreshComments: () => void;
}

type CommentItemProps = SharedCommentState & { comment: CommunityComment };

function CommentItem({ comment, ...shared }: CommentItemProps) {
  const {
    allComments,
    postAuthorName,
    user,
    isSubmitting,
    replyingTo,
    setReplyingTo,
    replyContent,
    setReplyContent,
    onSubmitReply,
    emojiTarget,
    setEmojiTarget,
    voiceTarget,
    setVoiceTarget,
    onVoiceSend,
  } = shared;

  const timestamp = useLiveTimestamp(comment.createdAt);
  const isAuthor = comment.user.name === postAuthorName;

  const isReplying = replyingTo === comment.id;
  const pickerOpen = emojiTarget === comment.id;
  const isVoiceMode = voiceTarget === comment.id;

  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const replyEmojiButtonRef = useRef<HTMLButtonElement>(null);
  const [replyCaret, setReplyCaret] = useState<number | null>(null);

  useCaretRestore(replyCaret, setReplyCaret, replyInputRef);

  // Find nested replies for this comment
  const replies = allComments.filter(c => c.parentId === comment.id);

  const toggleReply = () => {
    setReplyingTo(isReplying ? null : comment.id);
    setReplyContent('');
    setVoiceTarget(null);
    setEmojiTarget(null);
  };

  const handleReplyEmoji = (emoji: string) => {
    const { next, caret } = insertEmojiAtCursor(replyInputRef.current, replyContent, emoji);
    setReplyContent(next);
    setReplyCaret(caret);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 py-1">
        <Avatar
          src={comment.user.avatar}
          name={comment.user.name}
          size={28}
          className="mt-0.5"
          username={comment.user.username || null}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold text-text-primary text-sm">{comment.user.name}</span>
            {isAuthor && (
              <span className="text-xs bg-surface-3 text-text-secondary px-1.5 py-0.5 rounded">Author</span>
            )}
            <span className="text-xs text-text-tertiary">• {timestamp}</span>
          </div>
          <p className="text-text-primary text-sm leading-relaxed break-words" style={{ overflowWrap: 'anywhere' }}>{comment.content}</p>
          <div className="flex items-center gap-4 mt-1">
            {/* Comment-like button hidden until proper schema lands.
                See Phase 2 backlog: post_comments needs a likes_count
                column AND a comment_likes (or post_comment_likes) join
                table with RLS + a count-syncing trigger. The API at
                /api/community/comments/[id]/like still references both
                and was 500ing on every click. Re-enabling = uncomment
                this button after the migration ships. */}
            {user && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-text-tertiary hover:text-accent text-xs"
                onClick={toggleReply}
              >
                <Reply className="h-3.5 w-3.5" />
                <span className="ml-1">Reply</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Reply Form */}
      {isReplying && user && (
        <div className="ml-9 space-y-2">
          {isVoiceMode ? (
            // Voice recording mode
            <VoiceRecorderInput
              onVoiceSend={(audioBlob: Blob) => onVoiceSend(audioBlob, comment.id)}
              onCancel={() => {
                setVoiceTarget(null);
                toggleReply(); // Close reply form
              }}
              targetId={comment.id}
            />
          ) : (
            // Text reply mode
            <>
              <Textarea
                ref={replyInputRef}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                className="bg-surface-3 border-border-default text-text-primary placeholder:text-text-tertiary min-h-[60px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    onSubmitReply(comment.id);
                  }
                }}
              />
              {pickerOpen && (
                <EmojiPickerPopover
                  anchorRef={replyEmojiButtonRef}
                  onSelect={handleReplyEmoji}
                  onClose={() => setEmojiTarget(null)}
                />
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    ref={replyEmojiButtonRef}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setEmojiTarget(pickerOpen ? null : comment.id)}
                    aria-expanded={pickerOpen}
                    aria-haspopup="dialog"
                    aria-label="Emoji"
                    className="w-7 h-7 grid place-items-center rounded-sm text-text-tertiary hover:text-text-secondary hover:bg-surface-3 transition-colors"
                  >
                    <Smile className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setVoiceTarget(comment.id)}
                    title="Record voice reply"
                    aria-label="Record voice reply"
                    className="w-7 h-7 grid place-items-center rounded-sm text-text-tertiary hover:text-accent hover:bg-surface-3 transition-colors"
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-text-tertiary hover:text-text-secondary"
                    onClick={toggleReply}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-accent hover:bg-accent-hover text-text-on-accent"
                    onClick={() => onSubmitReply(comment.id)}
                    disabled={!replyContent.trim() || isSubmitting}
                  >
                    {isSubmitting ? 'Posting...' : 'Reply'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recursive Nested Replies — each level resolves its own reply, emoji
          and voice state from the shared id-keyed state. */}
      {replies.length > 0 && (
        <div className="ml-9 space-y-2 border-l-2 border-border-subtle pl-4 mt-2">
          {replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} {...shared} />
          ))}
        </div>
      )}
    </div>
  );
}
